// src/pages/inventory/Pharmacy/PatientMedicineAdministrationModal.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Divider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { administerToPatient } from "../../../services/patientMedicineInventoryService";
import { useAuth } from "../../../context/AuthContext";
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../../utils/formModalStyles";

const PatientMedicineAdministrationModal = ({
  open,
  onClose,
  patientInventory,
  onSubmit,
  patientId,
  admissionId,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    quantity: "",
    administeredDate: dayjs(),
    administeredTime: dayjs(),
    notes: "",
    administeredBy: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && patientInventory) {
      // Auto-populate administeredBy with logged-in user name
      const userName = user?.name || user?.username || "Unknown";
      setFormData({
        quantity: "",
        administeredDate: dayjs(),
        administeredTime: dayjs(),
        notes: "",
        administeredBy: userName,
      });
      setError("");
    }
  }, [open, patientInventory, user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.quantity || formData.quantity <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    if (parseInt(formData.quantity) > patientInventory.remainingQuantity) {
      setError(`Cannot administer more than ${patientInventory.remainingQuantity} units`);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const administrationData = {
        patientInventoryId: patientInventory.id,
        patientId: patientId,
        admissionId: admissionId,
        medicineId: patientInventory.medicineId,
        medicineName: patientInventory.medicineName,
        quantity: parseInt(formData.quantity),
        administeredDate: formData.administeredDate.toISOString(),
        administeredTime: formData.administeredTime.toISOString(),
        notes: formData.notes,
        administeredBy: formData.administeredBy,
      };

      await administerToPatient(administrationData);
      
      if (onSubmit) {
        await onSubmit();
      }
      
      onClose();
    } catch (error) {
      console.error("Error administering medicine:", error);
      setError(error.message || "Failed to administer medicine. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!patientInventory) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth sx={STANDARD_FORM_DIALOG_PAPER_SX}>
      <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
        Give Medicine to Patient
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: "primary.main" }}>
            Medicine Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Medicine Name
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {patientInventory.medicineName}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Available Quantity
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  {patientInventory.remainingQuantity} units
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Dosage
                </Typography>
                <Typography variant="body1">
                  {patientInventory.dosage || "N/A"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Batch Number
                </Typography>
                <Typography variant="body1">
                  {patientInventory.batchNumber || "N/A"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Expiry Date
                </Typography>
                <Typography variant="body1">
                  {patientInventory.expiryDate ? dayjs(patientInventory.expiryDate).format("DD MMM YYYY") : "N/A"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: "primary.main" }}>
            Administration Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity to Administer"
                type="number"
                value={formData.quantity}
                onChange={(e) => handleInputChange("quantity", e.target.value)}
                inputProps={{ min: 1, max: patientInventory.remainingQuantity }}
                helperText={`Maximum: ${patientInventory.remainingQuantity} units`}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Administered By"
                value={formData.administeredBy}
                InputProps={{ readOnly: true }}
                // helperText="Auto-filled with logged-in user"
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Administration Date & Time"
                  value={formData.administeredDate}
                  onChange={(newValue) => handleInputChange("administeredDate", newValue)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth disabled={loading} />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Enter any notes about the administration"
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> This will reduce the available quantity in the ward inventory 
            and record the administration to the patient. This action cannot be undone.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
        <Button onClick={handleClose} disabled={loading} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={FORM_SUBMIT_BUTTON_SX}
          disabled={loading || !formData.quantity}
        >
          {loading ? <CircularProgress size={20} /> : "Give Medicine to Patient"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientMedicineAdministrationModal;
